import React, { useEffect, useState } from "react";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";

function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const API_HOST = API_BASE.replace(/\/api\/?$/, "");

  const [currentProduct, setCurrentProduct] = useState(null);

  const [form, setForm] = useState({
    name: "",
    sku: "",
    category: "",
    unit: "",
    price: "",
    negotiation_allowed: true,
    is_active: true,
  });

  // Load all products
  const loadProducts = async () => {
    const res = await fetch(`${API_BASE}/products/list`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    setProducts(Array.isArray(data) ? data : []);
  };

  const loadMasters = async () => {
    try {
      const [catRes, unitRes] = await Promise.all([
        fetch(`${API_BASE}/masters/product-categories`, {
          headers: authHeaders(),
        }),
        fetch(`${API_BASE}/masters/product-units`, {
          headers: authHeaders(),
        }),
      ]);

      const catData = await catRes.json();
      const unitData = await unitRes.json();

      setCategories(Array.isArray(catData.data) ? catData.data : []);
      setUnits(Array.isArray(unitData.data) ? unitData.data : []);
    } catch (err) {
      console.error("Load product masters error:", err);
    }
  };

  useEffect(() => {
    loadProducts();
    loadMasters();
  }, []);

  const uploadProductPhoto = async (productId) => {
    if (!photoFile) return;

    const formData = new FormData();
    formData.append("photo", photoFile);

    const res = await fetch(`${API_BASE}/products/${productId}/photo`, {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.message || "Photo upload failed");
    }
  };

  // Save (Add or Update)
  const saveProduct = async () => {
    if (form.name === "" || form.sku === "" || form.price === "") {
      alert("Name, SKU & Price are required");
      return;
    }

    let url = `${API_BASE}/products/add`;
    let method = "POST";

    if (editMode) {
      url = `${API_BASE}/products/${editId}`; // ✅ FIXED
      method = "PUT";
    }

    const res = await fetch(url, {
      method,
      headers: authHeaders({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(
        editMode
          ? {
              price: form.price,
              negotiation_allowed: form.negotiation_allowed,
              is_active: form.is_active,
            }
          : form
      ),
    });

    if (!res.ok) {
      const text = await res.text();
      alert(`Error ${res.status}: ${text}`);
      return;
    }

    const data = await res.json();

    const productId = editMode ? editId : data?.product?.id;

    try {
      await uploadProductPhoto(productId);
    } catch (e) {
      alert(e.message);
    }

    setShowModal(false);
    setPhotoFile(null);
    setPhotoPreview(null);
    setCurrentProduct(null);

    setForm({
      name: "",
      sku: "",
      category: "",
      unit: "",
      price: "",
      negotiation_allowed: true,
      is_active: true,
    });

    setEditMode(false);
    setEditId(null);
    loadProducts();
  };

  // Delete Product
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;

    await fetch(`${API_BASE}/products/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });

    loadProducts();
  };

  // Edit Product
  const openEdit = (p) => {
    setCurrentProduct(p);
    setPhotoPreview(null);
    setPhotoFile(null);

    setForm({
      name: p.name,
      sku: p.sku,
      category: p.category,
      unit: p.unit,
      price: p.price,
      negotiation_allowed: p.negotiation_allowed,
      is_active: p.is_active,
    });

    setEditId(p.id);
    setEditMode(true);
    setShowModal(true);
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Products</h2>

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 15 }}>
        <button
          style={{
            padding: "10px 20px",
            background: "#0066ff",
            color: "white",
            border: "none",
            borderRadius: "6px",
          }}
          onClick={() => {
            setCurrentProduct(null);
            setPhotoFile(null);
            setPhotoPreview(null);
            setEditMode(false);
            setEditId(null);
            setForm({
              name: "",
              sku: "",
              category: "",
              unit: "",
              price: "",
              negotiation_allowed: true,
              is_active: true,
            });
            setShowModal(true);
          }}
        >
          Add Product
        </button>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show Inactive
        </label>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr style={{ background: "#ddd" }}>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>SKU</th>
            <th style={thStyle}>Category</th>
            <th style={thStyle}>Unit</th>
            <th style={thStyle}>Price</th>
            <th style={thStyle}>Photo</th>
            <th style={thStyle}>Negotiation</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {products
            .filter((p) => (showInactive ? true : p.is_active))
            .map((p) => (
              <tr key={p.id} style={{ borderBottom: "1px solid #ccc" }}>
                <td style={tdStyle}>{p.name}</td>
                <td style={tdStyle}>{p.sku}</td>
                <td style={tdStyle}>{p.category}</td>
                <td style={tdStyle}>{p.unit}</td>
                <td style={tdStyle}>₹{p.price}</td>

                <td style={tdStyle}>
                  {p.photo_url ? (
                    <img
                      src={`${API_HOST}${p.photo_url}`}
                      alt={p.name}
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: "cover",
                        borderRadius: 6,
                        display: "block", // ✅ important to keep cell height stable
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: 12, color: "#888" }}>No photo</span>
                  )}
                </td>

                <td style={tdStyle}>{p.negotiation_allowed ? "Yes" : "No"}</td>
                <td style={tdStyle}>{p.is_active ? "Active" : "Inactive"}</td>

                <td style={tdStyle}>
                  <button
                    onClick={() => openEdit(p)}
                    style={{
                      marginRight: "10px",
                      padding: "6px 12px",
                      background: "green",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                    }}
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => deleteProduct(p.id)}
                    style={{
                      padding: "6px 12px",
                      background: "red",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "400px",
              background: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <h3>{editMode ? "Edit Product" : "Add Product"}</h3>

            {/* INPUTS */}
            <input
              placeholder="Product Name"
              value={form.name}
              disabled={editMode}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={inputStyle}
            />

            <input
              placeholder="SKU"
              disabled={editMode}
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              style={inputStyle}
            />

            <select
              name="category"
              value={form.category || ""}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>

            <select
              name="unit"
              value={form.unit || ""}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select Unit</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.short_name || unit.name}>
                  {unit.short_name ? `${unit.name} (${unit.short_name})` : unit.name}
                </option>
              ))}
            </select>

            <input
              placeholder="Price"
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              style={inputStyle}
            />

            {/* FILE INPUT */}
            <label>Product Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                setPhotoFile(file);
                setPhotoPreview(URL.createObjectURL(file));
              }}
            />

            {/* PREVIEW */}
            <div style={{ marginTop: 8 }}>
              {(photoPreview || currentProduct?.photo_url) && (
                <img
                  src={
                    photoPreview ? photoPreview : `${API_HOST}${currentProduct.photo_url}`
                  }
                  alt="preview"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              )}
            </div>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <input
                type="checkbox"
                checked={form.negotiation_allowed}
                onChange={(e) =>
                  setForm({
                    ...form,
                    negotiation_allowed: e.target.checked,
                  })
                }
              />
              &nbsp; Negotiation Allowed?
            </label>

            <label style={{ display: "block", marginBottom: "20px" }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              &nbsp; Active Product?
            </label>

            <button onClick={saveProduct} style={saveBtn}>
              Save
            </button>

            <button onClick={() => setShowModal(false)} style={cancelBtn}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px",
  marginBottom: "10px",
  border: "1px solid #ccc",
};

const thStyle = { padding: "10px", textAlign: "left", borderBottom: "1px solid #ccc" };
const tdStyle = { padding: "10px", verticalAlign: "middle" };

const saveBtn = {
  background: "#0066ff",
  color: "white",
  padding: "10px",
  width: "100%",
  border: "none",
  borderRadius: "6px",
};

const cancelBtn = {
  background: "grey",
  color: "white",
  padding: "10px",
  width: "100%",
  border: "none",
  borderRadius: "6px",
  marginTop: "10px",
};

export default Products;